import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * AdminCurtainArrows
 *
 * Mirrors the gold left/right pagination arrows that visitors see on the
 * curtain-swipe pages — but for ADMIN MODE (when "Preview as Visitor" is OFF).
 *
 * Why:
 *   - In visitor mode, admins use the CurtainNavigation horizontal swipe deck
 *     with its built-in <ChevronLeft/Right> arrows. They can flip between
 *     pages quickly to see what a patient experiences.
 *   - In default admin mode, App.js renders each curtain page as a static
 *     scrollable view (so admins can review/QA each one). The CurtainNavigation
 *     arrows never mount, so admins had to hop into the AdminPreviewBar menu
 *     to jump pages. Slow.
 *
 * Solution:
 *   - Render the same fixed-position gold arrow buttons on the page edges
 *     when an admin is on a curtain route AND not in preview-as-visitor mode.
 *   - Reuses the existing `.curtain-nav-arrow` CSS so the visuals are literally
 *     identical — admins see one consistent control across both modes.
 *   - Wraps from last page → first and first → last, matching CurtainNavigation.
 */

// Order MUST match BASE_CURTAIN_PAGES in CurtainNavigation.jsx + CURTAIN_PATHS in App.js
const CURTAIN_ROUTES = [
  '/home',
  '/about-us',
  '/founders',
  '/cadence-promise',
  '/why-we-exist',
  '/pricing',
  '/membership',
  '/articles',
];

const AdminCurtainArrows = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const currentIndex = CURTAIN_ROUTES.indexOf(location.pathname);
  // Bail if we're not on a curtain page (shouldn't happen because the parent
  // gates on this, but defense-in-depth).
  if (currentIndex === -1) return null;

  const goPrev = () => {
    const nextIdx = (currentIndex - 1 + CURTAIN_ROUTES.length) % CURTAIN_ROUTES.length;
    navigate(CURTAIN_ROUTES[nextIdx]);
  };
  const goNext = () => {
    const nextIdx = (currentIndex + 1) % CURTAIN_ROUTES.length;
    navigate(CURTAIN_ROUTES[nextIdx]);
  };

  return (
    <>
      <button
        type="button"
        className="curtain-nav-arrow curtain-nav-arrow-left"
        onClick={goPrev}
        aria-label="Previous page"
        data-testid="admin-nav-arrow-left"
      >
        <ChevronLeft size={32} />
      </button>
      <button
        type="button"
        className="curtain-nav-arrow curtain-nav-arrow-right"
        onClick={goNext}
        aria-label="Next page"
        data-testid="admin-nav-arrow-right"
      >
        <ChevronRight size={32} />
      </button>
    </>
  );
};

export default AdminCurtainArrows;
